import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Star, TrendingUp } from "lucide-react";
import { BusinessCard, BusinessCardCompact } from "@/components/BusinessCard";
import { analytics } from "@/utils/analytics";

const API_URL = '';

const FOOD_FILTERS = [
  { id: "all", label: "All" },
  { id: "Cafe", label: "Cafe" },
  { id: "Italian", label: "Italian" },
  { id: "Asian", label: "Asian" },
  { id: "Fine Dining", label: "Fine Dining" },
  { id: "Bar & Grill", label: "Bar & Grill" },
  { id: "Seafood", label: "Seafood" },
];

// Shuffle array for fair rotation within tier
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Fair sort: Featured first (shuffled), then standard (shuffled)
const fairSort = (businesses) => {
  const featured = businesses.filter(b => b.is_featured);
  const standard = businesses.filter(b => !b.is_featured);
  return [...shuffleArray(featured), ...shuffleArray(standard)];
};

export default function EatCategoryPage() {
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [trendingBusinesses, setTrendingBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    // Track category view
    analytics.trackCategoryView('eat');
    fetchAllBusinesses();
  }, []);

  useEffect(() => {
    // Filter and apply fair sorting
    let filtered = allBusinesses;
    if (activeFilter !== "all") {
      filtered = allBusinesses.filter(b => b.food_type === activeFilter);
    }
    setFilteredBusinesses(fairSort(filtered));
  }, [activeFilter, allBusinesses]);

  const fetchAllBusinesses = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/businesses?category=eat`);
      if (response.ok) {
        const data = await response.json();
        setAllBusinesses(data);
        setFilteredBusinesses(fairSort(data));
        
        // Trending = shuffled selection for editorial section (not featuring only featured)
        setTrendingBusinesses(shuffleArray(data).slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
  };

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-28" data-testid="eat-category-page">
      {/* Compact Header - 56px height */}
      <header className="sticky top-0 z-40 glass-header h-14 px-5">
        <div className="max-w-lg mx-auto h-full flex items-center gap-3">
          <Link 
            to="/" 
            className="w-9 h-9 rounded-xl bg-slate-100/80 flex items-center justify-center nav-icon-glow"
            aria-label="Back to home" data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 text-slate-700" />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold text-slate-900">
              Eat
            </h1>
            <p className="text-xs text-slate-500">
              Discover dining on the Gold Coast
            </p>
          </div>
        </div>
      </header>

      {/* Places to discover - Editorial Carousel (neutral, not paid placement) */}
      {trendingBusinesses.length > 0 && (
        <section className="pt-6 pb-2">
          <div className="max-w-lg mx-auto px-5 mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-slate-500" />
              <h2 className="text-lg font-semibold text-slate-900">
                Places to discover
              </h2>
            </div>
          </div>
          
          <div 
            className="flex gap-3 px-5 overflow-x-auto pb-2 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {trendingBusinesses.map((business) => (
              <BusinessCardCompact key={`trending-${business.business_id}`} business={business} />
            ))}
            <div className="flex-shrink-0 w-2" />
          </div>
        </section>
      )}

      {/* Cuisine Filter Row */}
      <section className="pt-4 pb-2">
        <div 
          className="flex gap-2.5 px-5 overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {FOOD_FILTERS.map((filter) => {
            const isActive = activeFilter === filter.id;
            return (
              <button
                key={filter.id}
                onClick={() => handleFilterChange(filter.id)}
                className={`
                  flex-shrink-0 px-5 py-2.5 rounded-[20px] text-sm font-medium
                  pill-glow
                  ${isActive
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-slate-600 shadow-sm shadow-black/5'
                  }
                `}
                aria-pressed={isActive}
                data-testid={`filter-${filter.id}`}
              >
                {filter.label}
              </button>
            );
          })}
          <div className="flex-shrink-0 w-2" />
        </div>
      </section>

      {/* Restaurant Feed - Fair rotation within tiers */}
      <main className="px-5 pt-5">
        <div className="max-w-lg mx-auto">
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-4">
            {loading ? 'Loading...' : `${filteredBusinesses.length} ${filteredBusinesses.length === 1 ? 'place' : 'places'}`}
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredBusinesses.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-400 mb-3">No places found</p>
              <button 
                onClick={() => setActiveFilter("all")}
                className="text-teal-600 text-sm font-medium hover:underline"
              >
                View all restaurants
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredBusinesses.map((business) => (
                <BusinessCard 
                  key={business.business_id} 
                  business={business}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Hide scrollbar CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
